import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { SelfExamMarksResponse } from '@bbl/validation';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';
import { SelfMarksList } from '../components/exams';
import { GatedSection } from '../components/shared/GatedSection';

export const Route = createFileRoute('/_authed/me/marks')({
  component: MyMarksPage,
});

function MyMarksPage() {
  const role = useAuthStore((s) => s.user?.primaryRole);
  const isStudent = role === 'student';

  const marksQuery = useQuery({
    queryKey: ['me', 'exam-marks'] as const,
    queryFn: ({ signal }) =>
      apiRequest<SelfExamMarksResponse>('/api/me/exam-marks', { signal }),
    enabled: isStudent,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  if (role && !isStudent) {
    return (
      <ListShell title="My marks" description="Student self-service.">
        <EmptyState
          title="This page is for students"
          description="Parents can view their child's marks from the children list."
          action={
            <Link to="/dashboard">
              <Button variant="secondary">Back to dashboard</Button>
            </Link>
          }
        />
      </ListShell>
    );
  }

  return (
    <ListShell
      title="My marks"
      description="Your published exam results grouped by cycle."
    >
      <GatedSection
        error={marksQuery.error}
        title="My marks"
        description="Once your academy enables exams and publishes a cycle, your marks will appear here."
        onRetry={() => marksQuery.refetch()}
      >
        {marksQuery.isLoading || !marksQuery.data ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <SelfMarksList items={marksQuery.data.items} />
        )}
      </GatedSection>
    </ListShell>
  );
}
